/**
 * The authenticated fetch every admin module shares.
 *
 * Lifted out of `enterprise/api.ts`, where it was module-private and GET-only. The access panel needs
 * POST and DELETE, and a second copy of an auth helper is not a duplication that stays harmless: the
 * two drift, and the drift is a security bug. So one helper, one place, both callers.
 *
 * Auth is the session cookie (`credentials: 'include'`), matching every other service in the app.
 */
import { API_CONFIG } from '../../config/constants';

const API_URL: string = API_CONFIG.BASE_URL || '';

export interface ApiResponse<T> {
  data: T | null;
  error: string | null;
}

type Method = 'GET' | 'POST' | 'PATCH' | 'DELETE';

/**
 * `error` carries the server's `detail` when there is one.
 *
 * That matters more here than in a read-only dashboard: this API's refusals are the useful part. A 409
 * on a contested domain names the client that already holds it, and a 422 on a public email domain
 * explains why — surfacing "HTTP 409" instead would throw away the only guidance the operator gets.
 */
export async function adminFetch<T>(
  path: string,
  method: Method = 'GET',
  body?: unknown,
): Promise<ApiResponse<T>> {
  try {
    const response = await fetch(`${API_URL}${path}`, {
      method,
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });

    if (!response.ok) {
      const payload = await response.json().catch(() => ({}));
      const detail = (payload as { detail?: unknown }).detail;
      return {
        data: null,
        error:
          typeof detail === 'string'
            ? detail
            : detail
              ? JSON.stringify(detail)
              : `HTTP ${response.status}`,
      };
    }

    // 204 and an empty 200 both mean "it worked and there is nothing to read".
    const text = await response.text();
    return { data: text ? (JSON.parse(text) as T) : (null as T), error: null };
  } catch (error) {
    return {
      data: null,
      error: error instanceof Error ? error.message : 'Request failed',
    };
  }
}
